import { defineMessages, type IntlShape } from 'react-intl';
import type { ToastAction } from '../../contexts/ToastContext';
import type { DisplayJob } from '../../types';
import type { FollowedCompany } from '../user-state/extract-followed-companies-from-user-state';
import { trackEvent } from '../analytics/analytics';

const messages = defineMessages({
  savedAlsoFollow: {
    id: 'jobDetail.save.alsoFollowToast',
    defaultMessage: 'Job saved. Follow {company} to hear about their next roles?',
    description: 'Toast after saving a job, offering to also follow the employer',
  },
  followAction: {
    id: 'jobDetail.save.alsoFollowAction',
    defaultMessage: 'Follow',
    description: 'Toast action that follows the employer after saving one of their jobs',
  },
  nowFollowing: {
    id: 'jobDetail.save.nowFollowingToast',
    defaultMessage: 'Following {company}. New roles will show on your watchlist.',
    description: 'Toast confirming the employer was followed from the save-job offer',
  },
});

export interface SaveFollowOfferParams {
  intl: IntlShape;
  formatMessage: IntlShape['formatMessage'];
  showToast: (message: string, type?: 'success' | 'error' | 'info', duration?: number, action?: ToastAction) => void;
  displayJob: DisplayJob;
  followedCompanies: FollowedCompany[];
  followCompany: (companyId: string, extras?: { companyName?: string }) => Promise<boolean>;
}

/**
 * Saving a job is the highest-intent moment for a company follow — when the
 * saver doesn't already follow the employer, the save confirmation doubles as
 * a follow offer (replacing the View → action for that one save). Returns true
 * when the offer toast was shown; false means the caller should show its
 * standard save toast. Shared by the job page's save handler and useJobActions
 * so the two (historically duplicated) save paths stay in sync.
 */
export function maybeShowSaveFollowOfferToast(params: SaveFollowOfferParams): boolean {
  const { intl, formatMessage, showToast, displayJob, followedCompanies, followCompany } = params;
  const companyId = displayJob.companyId;
  if (!companyId || followedCompanies.some((c) => c.companyId === companyId)) {
    return false;
  }

  const companyLabel = displayJob.companyName || companyId;
  showToast(
    formatMessage(messages.savedAlsoFollow, { company: companyLabel }),
    'success',
    6000,
    {
      label: formatMessage(messages.followAction),
      onClick: () => {
        trackEvent({ name: 'company_follow', params: { company_id: companyId, surface: 'save-toast', locale: intl.locale } });
        void followCompany(companyId, { companyName: displayJob.companyName }).then((ok) => {
          if (ok) {
            showToast(formatMessage(messages.nowFollowing, { company: companyLabel }), 'success');
          }
        });
      },
    }
  );
  return true;
}
